import {
  ROUTES,
  adminRuleVersionActivatePath,
  adminRuleVersionPath,
  type AdminActivateVersionResponse,
  type AdminDecisionsPageResponse,
  type AdminDryRunRequest,
  type AdminDryRunResponse,
  type AdminRuleVersionGetResponse,
  type AdminRuleVersionsResponse,
  type AdminRulesGetResponse,
  type AdminRulesPutResponse,
  type AdminStatsResponse,
  type DecisionAudit,
  type MasterAddItemResponse,
  type MasterAttrs,
  type MasterCreateRequest,
  type MasterItemUpdateResponse,
  type MasterTemplate,
  type MasterTemplateInferResult,
  type MasterTemplateRequest,
  type MasterImportMode,
  type MasterImportResult,
  type MasterItemsPage,
  type MasterList,
  type MasterRemoveItemResponse,
  type MasterUpdateRequest,
  type PreDetectRequest,
  type RuleSet,
  type RuleValidation,
} from '@kkp-fraud/shared';
import { parseAlertsStatus, type AlertTestResult, type AlertsStatus } from './alerts';
import type { AuditEntry } from './audit';
import { hasKey, t } from './i18n';
import { AUTH_MODE, authRequestInit, forgetCredential } from './auth';
import { DEMO, withDemo } from './demo';
import { collectRange, type DecisionRange, type DecisionsRangeResult, type FetchDecisionsPage, type OnRangePage } from './decisionsRange';
import type {
  Proposal,
  ProposalPublishResponse,
  ProposalReplayResponse,
  ProposalValidateResponse,
} from './proposals';
import type { Session } from './session';
// R9 cases
import {
  CASE_ROUTES,
  caseQuery,
  casePath,
  type Case,
  type CaseFilters,
  type CasePage,
  type CasePriority,
  type CaseStatus,
  type InvestigatePage,
  type RulePrecisionReport,
} from './cases';

/**
 * The typed client for whichever admin surface this build talks to. EVERY fetch the console
 * makes goes through `request()` below — including the static sample files — so there is
 * exactly one place that knows how a call is addressed and how it is authenticated.
 *
 * Two surfaces, one client:
 *
 *   password mode  →  the decision API's own `/admin/*`, with `x-admin-password` from the
 *                     browser. Local development without the BFF; unchanged by the cutover.
 *   cookie mode    →  the BFF's `/api/*`, same origin, session cookie + `X-CSRF-Token`. The
 *                     browser holds no credential at all; the BFF adds `x-admin-password`
 *                     server-side on the hop to the decision API.
 *
 * Credentials themselves live in `auth.ts`; this module only asks it for the request init.
 */

export interface ApiSettings {
  /** Empty means "same origin" — the Vite dev proxy forwards /admin to the API. */
  baseUrl: string;
  /**
   * 2c rule sets — the set the rule calls address (`/api/rulesets/{id}/rules…`). Absent = the
   * primary, through today's `/api/rules…`. Set only by the Rule Management screens for a set
   * that is not the primary, cookie mode only, and never persisted with the settings.
   */
  ruleSetId?: string;
}

export const DEFAULT_SETTINGS: ApiSettings = {
  baseUrl: import.meta.env?.VITE_API_URL ?? '',
};

/** True for a build that talks to the BFF. Read it instead of comparing AUTH_MODE by hand. */
export const COOKIE_MODE = AUTH_MODE === 'cookie';

/**
 * Credential handling moved to `auth.ts` (AUTH_MODE). Re-exported here so existing call
 * sites keep working unchanged and there is still one import path for the UI.
 */
export {
  AUTH_MODE,
  clearAdminPassword,
  getAdminPassword,
  holdsClientCredential,
  isAuthenticated,
  setAdminPassword,
  type AuthMode,
} from './auth';

// =============================================================================
// The route map — the whole cutover, in one table
// =============================================================================

interface RouteMap {
  rules: string;
  validate: string;
  dryRun: string;
  decisions: string;
  stats: string;
  ruleVersions: string;
  readyz: string;
  version: (v: string) => string;
  /** Rollback: releases a stored snapshot again as a new version. */
  activateVersion: (v: string) => string;
}

/**
 * Cookie mode. Every one of these is same-origin against the BFF — see
 * `dotnet/admin-api/README.md` "Routes". They are spelled here rather than in
 * `packages/shared` on purpose: `shared` is the contract between the ENGINE and its clients,
 * and `/api/*` is the BFF's own surface, which the engine knows nothing about.
 */
const COOKIE_ROUTES: RouteMap = {
  rules: '/api/rules',
  validate: '/api/rules/validate',
  dryRun: '/api/rules/dry-run',
  decisions: '/api/decisions',
  stats: '/api/stats',
  ruleVersions: '/api/rules/versions',
  // Engine readiness comes from the proxied /readyz, not /healthz: readyz is the one that
  // reports the rule set version, its source and the state store.
  readyz: '/api/readyz',
  version: (v) => `/api/rules/versions/${encodeURIComponent(v)}`,
  activateVersion: (v) => `/api/rules/versions/${encodeURIComponent(v)}/activate`,
};

/** Password mode — the decision API's own routes, from the shared contract. */
const PASSWORD_ROUTES: RouteMap = {
  rules: ROUTES.adminRules,
  validate: ROUTES.adminValidate,
  dryRun: ROUTES.adminDryRun,
  decisions: ROUTES.adminDecisions,
  stats: ROUTES.adminStats,
  ruleVersions: ROUTES.adminRuleVersions,
  readyz: ROUTES.readyz,
  version: adminRuleVersionPath,
  activateVersion: adminRuleVersionActivatePath,
};

/** The routes THIS build uses. Exported so a test can assert the map rather than guess it. */
export const API_ROUTES: RouteMap = COOKIE_MODE ? COOKIE_ROUTES : PASSWORD_ROUTES;

// 2c rule sets
/** The BFF's routes for one set's rules — the same map as `/api/rules…`, under `/api/rulesets/{id}`. */
export function ruleSetRoutes(setId: string): RouteMap {
  const base = `/api/rulesets/${encodeURIComponent(setId)}/rules`;
  return {
    ...COOKIE_ROUTES,
    rules: base,
    validate: `${base}/validate`,
    dryRun: `${base}/dry-run`,
    ruleVersions: `${base}/versions`,
    version: (v) => `${base}/versions/${encodeURIComponent(v)}`,
    activateVersion: (v) => `${base}/versions/${encodeURIComponent(v)}/activate`,
  };
}

/** The routes a rule call of `s` goes to: its set's (cookie mode, a set named), else `API_ROUTES`. */
export function rulesRoutesFor(s: ApiSettings): RouteMap {
  return COOKIE_MODE && s.ruleSetId ? ruleSetRoutes(s.ruleSetId) : API_ROUTES;
}

/** The BFF's session routes. They do not exist in password mode and are never called there. */
export const AUTH_ROUTES = {
  login: '/auth/login',
  logout: '/auth/logout',
  session: '/api/session',
  proposals: '/api/proposals',
  /** The access trail — `GET /api/audit?limit=`. */
  audit: '/api/audit',
  /** What the alert dispatcher says about itself, per channel kind. */
  alertStatus: '/api/alerts/status',
  /** Editor only: one test email to a channel's addresses. */
  alertTest: '/api/alerts/test-email',
  /** Master lists (named sets a rule references by id). The BFF's own surface — no engine route. */
  masters: '/api/masters',
} as const;

const masterPath = (id: string, suffix = ''): string =>
  `${AUTH_ROUTES.masters}/${encodeURIComponent(id)}${suffix}`;

const proposalPath = (id: string, suffix = ''): string =>
  `${AUTH_ROUTES.proposals}/${encodeURIComponent(id)}${suffix}`;

// =============================================================================
// Errors and the one fetch
// =============================================================================

/**
 * The shell registers a callback here so a 401 from ANY call — not only the login — drops
 * the user back to the login screen instead of leaving a half-dead control panel on screen.
 * QA-A-06: `reason` is the admin service's own, when it ended the session on purpose —
 * `credentials_changed` after a password change or reset elsewhere.
 */
let unauthorisedHandler: ((reason?: string) => void) | null = null;

export function onUnauthorised(handler: ((reason?: string) => void) | null): void {
  unauthorisedHandler = handler;
}

/**
 * QA-A-12: a 403 the admin service gives without saying which rule (a role it no longer has),
 * or `password_change_required` in the middle of a session — both mean "what this session may
 * do changed". The shell registers a callback that re-reads the session (roles, the password
 * gate), so the menus follow at once.
 */
let accessChangedHandler: (() => void) | null = null;

export function onAccessChanged(handler: (() => void) | null): void {
  accessChangedHandler = handler;
}

export class ApiError extends Error {
  readonly status: number;
  /** The machine-readable `error` field: `upstream_auth`, `maker_is_checker`, `csrf_failed`… */
  readonly code?: string;
  /** The `validation` object the API returns on a 400 from PUT / dry-run. */
  readonly validation?: RuleValidation;
  readonly problems?: { field: string; message: string; code?: string }[];
  /** On a 409 from PUT /admin/rules — the version the engine is actually running. */
  readonly activeVersion?: string;
  /** On a 409 `master_in_use` — the active rules that reference the list. */
  readonly rules?: string[];
  /** R11, on a 409 `template_in_use` — the lists (and the columns) that depend on the change. */
  readonly lists?: string[];
  readonly columns?: string[];
  // R8b per-rule-set permissions
  /** On a 403 `ruleset_forbidden` — the level the route needs (`view` | `edit` | `publish` | `admin`). */
  readonly needs?: string;
  /** QA-A-08: on a 401 `account_locked` — whole minutes until the sign-in may be tried again. */
  readonly retryAfterMinutes?: number;
  /** A finer reason beside the code — QA-A-05: `required_column` on a 409 `template_in_use`. */
  readonly reason?: string;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    const b = body as
      | {
          error?: string;
          validation?: RuleValidation;
          problems?: { field: string; message: string; code?: string }[];
          activeVersion?: string;
          rules?: unknown;
          lists?: unknown;
          columns?: unknown;
        }
      | undefined;
    if (typeof b?.error === 'string') this.code = b.error;
    if (b?.validation) this.validation = b.validation;
    if (b?.problems) this.problems = b.problems;
    if (b?.activeVersion) this.activeVersion = b.activeVersion;
    if (Array.isArray(b?.rules)) this.rules = b.rules.filter((r): r is string => typeof r === 'string');
    // R11 master templates
    if (Array.isArray(b?.lists)) this.lists = b.lists.filter((r): r is string => typeof r === 'string');
    if (Array.isArray(b?.columns)) this.columns = b.columns.filter((r): r is string => typeof r === 'string');
    // R8b per-rule-set permissions
    const needs = (body as { needs?: unknown } | undefined)?.needs;
    if (typeof needs === 'string') this.needs = needs;
    const retry = (body as { retryAfterMinutes?: unknown } | undefined)?.retryAfterMinutes;
    if (typeof retry === 'number' && Number.isFinite(retry)) this.retryAfterMinutes = retry;
    const reason = (body as { reason?: unknown } | undefined)?.reason;
    if (typeof reason === 'string') this.reason = reason;
  }
}

// R8b per-rule-set permissions
/** A 403 `ruleset_forbidden` in words (what the caller lacks), or null for any other error. */
export function ruleSetForbiddenMessage(e: unknown): string | null {
  if (!(e instanceof ApiError) || e.code !== 'ruleset_forbidden') return null;
  const needs = e.needs === 'view' || e.needs === 'edit' || e.needs === 'publish' || e.needs === 'admin' ? e.needs : 'view';
  return t(`perms.forbidden.${needs}`);
}

/**
 * A failure of the SERVER's own configuration, not of the person signing in.
 *
 *   502 `upstream_auth`      — the BFF's admin password is wrong, so the decision API rejected
 *                              IT. The BFF answers 502 precisely so a valid user is not bounced
 *                              to the login screen by somebody else's misconfiguration.
 *   503 `admin_not_configured` — the BFF has no `DecisionApi__AdminPassword` at all.
 *
 * Neither is anything the user can fix by typing a password again, so they are never shown
 * as a login error.
 */
export function isServerConfigError(e: unknown): e is ApiError {
  return (
    e instanceof ApiError &&
    ((e.status === 502 && e.code === 'upstream_auth') || (e.status === 503 && e.code === 'admin_not_configured'))
  );
}

/** A one-line explanation for the two above, for the shell's banner. */
export function serverConfigMessage(e: ApiError): string {
  if (e.status === 503) {
    return t('api.config.missing', { detail: e.message });
  }
  return t('api.config.rejected', { detail: e.message });
}

interface RequestOptions extends RequestInit {
  /**
   * `false` for same-origin static assets served beside the SPA (the dry-run samples).
   * They carry no credential in either auth mode — sending one would leak it to a path
   * that never needed it.
   */
  auth?: boolean;
  /** `false` for a path that is already absolute from the app root, not an API route. */
  useBaseUrl?: boolean;
  /**
   * `false` for the two calls whose whole job is to ASK whether there is a session — the
   * login and the session probe. Without this, a wrong password or a cold start would fire
   * `onUnauthorised` and bounce the login screen to itself saying the session had expired.
   */
  bounceOn401?: boolean;
  /** `'blob'` returns the body untouched — a file download (`res.text()` would drop a CSV's BOM). */
  responseType?: 'json' | 'blob';
}

async function request<T>(settings: ApiSettings, path: string, init?: RequestOptions): Promise<T> {
  const { auth = true, useBaseUrl = true, bounceOn401 = true, responseType = 'json', ...rest } = init ?? {};
  // Cookie mode is same-origin by construction: the BFF serves this app, and
  // `credentials: 'same-origin'` would not carry the session cookie anywhere else anyway.
  // A stale `baseUrl` in localStorage must not be able to break the cutover build.
  const base = COOKIE_MODE ? '' : settings.baseUrl.replace(/\/$/, '');
  const url = useBaseUrl ? `${base}${path}` : path;
  const authInit = auth ? authRequestInit() : { headers: {} as Record<string, string> };
  let res: Response;
  try {
    res = await fetch(url, {
      ...rest,
      ...(authInit.credentials ? { credentials: authInit.credentials } : {}),
      headers: {
        // A FormData body sets its own multipart Content-Type, boundary included.
        ...(rest.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...authInit.headers,
        ...(rest.headers ?? {}),
      },
    });
  } catch (e) {
    throw new ApiError(
      t('api.unreachable', { detail: (e as Error).message }),
      0,
    );
  }

  if (res.ok && responseType === 'blob') return (await res.blob()) as T;

  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }

  if (!res.ok) {
    const message =
      (body as { message?: string; error?: string } | null)?.message ??
      (body as { error?: string } | null)?.error ??
      `HTTP ${res.status}`;

    if (res.status === 401) {
      // Cookie mode: 401 means "no session" — never "wrong password". The BFF maps an
      // upstream credential failure to 502 so a server misconfiguration cannot masquerade
      // as an expired user session (README, "Upstream status mapping").
      forgetCredential();
      const b = body as { error?: unknown; reason?: unknown } | null;
      const reason = b?.error === 'session_revoked' && typeof b.reason === 'string' ? b.reason : undefined;
      if (bounceOn401) unauthorisedHandler?.(reason);
      throw new ApiError(
        COOKIE_MODE ? t(reason === 'credentials_changed' ? 'auth.revoked.credentials' : 'api.session.ended') : t('api.password.wrong'),
        401,
        body,
      );
    }
    if (COOKIE_MODE && res.status === 403) {
      const code = (body as { error?: unknown } | null)?.error;
      // A bare 403 is a role this session no longer has (the admin service re-reads roles on
      // every request); password_change_required mid-session is a reset elsewhere. Either
      // way the shell re-reads the session, and the screen gets words, never "HTTP 403".
      if (code === undefined || code === 'password_change_required') {
        accessChangedHandler?.();
        throw new ApiError(t('api.access.changed'), 403, body);
      }
    }
    if (!COOKIE_MODE && res.status === 503 && message === 'admin password not configured') {
      // Password mode only: this is the DECISION API's wording (server.ts:288). The BFF
      // never sends it — in cookie mode the same condition is 503 `admin_not_configured`,
      // handled as a server-config error rather than as anything about the password here.
      throw new ApiError(
        t('api.password.unset'),
        503,
        body,
      );
    }
    throw new ApiError(message, res.status, body);
  }
  return body as T;
}

const realApi = {
  ready: (s: ApiSettings) =>
    // 2c rule sets: `ruleSets` — every set the engine tried to load (absent on an older engine).
    // Wave 4: `apiClients` — the per-caller keys the engine loaded (absent on an older engine).
    request<{ status: string; stateStore: string; stateStoreOk: boolean; rulesLoaded: boolean; ruleSetVersion: string; rulesSource: string; loadedAt: string; shadowMode: boolean; ruleSets?: RuleSetStatus[]; apiClients?: ApiClientsStatus }>(
      s,
      API_ROUTES.readyz,
    ),

  // 2c rule sets: every rule call below addresses `s.ruleSetId`'s routes when one is named.
  getRules: (s: ApiSettings) =>
    DEMO && isDemoRuleSetId(s.ruleSetId) ? demoRuleSetDocument(s.ruleSetId!) : request<AdminRulesGetResponse>(s, rulesRoutesFor(s).rules),

  /**
   * Publish: the document goes live at once. `expectedVersion` is the version the draft was
   * loaded from — the API answers 409 if somebody else released in the meantime, instead of
   * silently overwriting them.
   *
   * Cookie mode sends no `actor`: the BFF stamps it from the session (and strips any the
   * browser sends). Password mode names the actor the operator typed.
   */
  putRules: (s: ApiSettings, ruleSet: RuleSet, actor: string, comment?: string, expectedVersion?: string) =>
    // `activeWithinSeconds`: only the admin service in Rules:Store=cosmos mode sends it — there it
    // writes the release itself and every engine replica picks it up by polling within that time.
    request<AdminRulesPutResponse & { activeWithinSeconds?: number }>(s, rulesRoutesFor(s).rules, {
      method: 'PUT',
      body: JSON.stringify({
        ruleSet,
        ...(COOKIE_MODE ? {} : { actor }),
        ...(comment ? { comment } : {}),
        ...(expectedVersion ? { expectedVersion } : {}),
      }),
    }),

  /**
   * The engine's verdict on a document (Wave 2-0: the only rule check there is). `signal`
   * lets a newer edit cancel a check still in flight (`useRuleCheck`).
   */
  validate: (s: ApiSettings, ruleSet: RuleSet, signal?: AbortSignal) =>
    request<RuleValidation>(s, rulesRoutesFor(s).validate, {
      method: 'POST',
      body: JSON.stringify({ ruleSet }),
      ...(signal ? { signal } : {}),
    }),

  dryRun: (s: ApiSettings, req: PreDetectRequest, ruleSet?: RuleSet) =>
    request<AdminDryRunResponse>(s, rulesRoutesFor(s).dryRun, {
      method: 'POST',
      body: JSON.stringify({ request: req, ...(ruleSet ? { ruleSet } : {}) } satisfies AdminDryRunRequest),
    }),

  /** The latest `limit` decisions — Dashboard, Monitor, Log. Not period-aware; Report uses `decisionsRange`. */
  decisions: (s: ApiSettings, limit = 100) =>
    request<DecisionAudit[]>(s, `${API_ROUTES.decisions}?limit=${encodeURIComponent(limit)}`),

  /** One page of `[from, to)`, newest first. `decisionsRange` is the loop over these. */
  decisionsPage: (s: ApiSettings, q: Parameters<FetchDecisionsPage>[0]) => {
    const qs = new URLSearchParams({ from: q.from, to: q.to, limit: String(q.limit), ...(q.cursor ? { cursor: q.cursor } : {}) });
    return request<AdminDecisionsPageResponse>(s, `${API_ROUTES.decisions}?${qs.toString()}`);
  },

  /**
   * Every decision whose event time is in `[from, to)`, paged through `nextCursor` up to
   * `RANGE_CEILING` rows (`capped` says the ceiling bit). `onPage` reports progress and may
   * return `false` to abandon the range.
   */
  decisionsRange: (s: ApiSettings, range: DecisionRange, onPage?: OnRangePage): Promise<DecisionsRangeResult> =>
    collectRange((q) => realApi.decisionsPage(s, q), range, onPage),

  stats: (s: ApiSettings) => request<AdminStatsResponse>(s, API_ROUTES.stats),

  // ---- version history ----------------------------------------------------
  versions: (s: ApiSettings) =>
    DEMO && isDemoRuleSetId(s.ruleSetId) ? demoRuleSetVersions(s.ruleSetId!) : request<AdminRuleVersionsResponse>(s, rulesRoutesFor(s).ruleVersions),

  version: (s: ApiSettings, version: string) =>
    DEMO && isDemoRuleSetId(s.ruleSetId)
      ? demoRuleSetDocument(s.ruleSetId!).then((r) => ({ version, savedUtc: r.loadedAt, actor: 'demo', ruleSet: r.ruleSet }))
      : request<AdminRuleVersionGetResponse>(s, rulesRoutesFor(s).version(version)),

  /**
   * Rollback. The API releases this snapshot's whole document as a NEW version, live at once.
   * Cookie mode sends no `actor` — the BFF names it from the session.
   */
  activateVersion: (s: ApiSettings, version: string, actor: string, comment?: string) =>
    // `activeWithinSeconds`: as for `putRules` — only the admin service in Rules:Store=cosmos mode sends it.
    request<AdminActivateVersionResponse & { activeWithinSeconds?: number }>(s, rulesRoutesFor(s).activateVersion(version), {
      method: 'POST',
      body: JSON.stringify({ ...(COOKIE_MODE ? {} : { actor }), ...(comment ? { comment } : {}) }),
    }),

  // ---- session (cookie mode only) -----------------------------------------

  /**
   * The first call the cutover build makes: "who is signed in?" — `null` for nobody.
   *
   * Signed out, the BFF answers `200 {"authenticated":false}`, so a cold start leaves no red
   * 401 in the browser console. An older BFF answers 401 instead; that means the same thing
   * and is read the same way. Neither fires `onUnauthorised`, or the login screen would
   * announce an expired session to somebody who never had one. Anything else — the BFF
   * unreachable, a 5xx — still throws.
   */
  session: async (s: ApiSettings): Promise<Session | null> => {
    let body: Session | { authenticated: false } | null;
    try {
      body = await request<Session | { authenticated: false } | null>(s, AUTH_ROUTES.session, { bounceOn401: false });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null;
      throw e;
    }
    // Only a body that names a user is a session; `{authenticated:false}` (or nothing) is not.
    if (!body || typeof body !== 'object' || body.authenticated === false || !('user' in body)) return null;
    return body;
  },

  /** 401 here means wrong username or password, and nothing else. Never bounces. */
  login: (s: ApiSettings, username: string, password: string) =>
    request<Session>(s, AUTH_ROUTES.login, {
      method: 'POST',
      body: JSON.stringify({ username, password }),
      bounceOn401: false,
    }),

  logout: (s: ApiSettings) => request<{ ok: boolean }>(s, AUTH_ROUTES.logout, { method: 'POST' }),

  /** The admin service's access trail, newest first. Cookie mode only: password mode has none. */
  audit: (s: ApiSettings, limit = 200): Promise<AuditEntry[]> =>
    COOKIE_MODE ? request<AuditEntry[]>(s, `${AUTH_ROUTES.audit}?limit=${encodeURIComponent(limit)}`) : Promise.resolve([]),

  // ---- alert delivery (cookie mode only) ----------------------------------

  /**
   * Channel state as the alert dispatcher reports it, through the BFF. `null` when there is
   * no BFF (password mode) or the answer is not the status shape — both read as "not
   * connected", never as connected.
   */
  alertStatus: async (s: ApiSettings): Promise<AlertsStatus | null> => {
    if (!COOKIE_MODE) return null;
    return parseAlertsStatus(await request<unknown>(s, AUTH_ROUTES.alertStatus));
  },

  /** Editor only (the BFF answers 403 otherwise). The BFF names the actor from the session. */
  sendTestAlert: (s: ApiSettings, body: { channelId: string; channelName: string; to: string[] }) =>
    request<AlertTestResult>(s, AUTH_ROUTES.alertTest, { method: 'POST', body: JSON.stringify(body) }),

  // ---- master lists (cookie mode only) ------------------------------------
  // The BFF's `/api/masters`. Password mode has no BFF: the list reads as empty and the screen
  // offers no writes (it never calls the others there).
  masters: {
    list: (s: ApiSettings): Promise<MasterList[]> =>
      COOKIE_MODE ? request<MasterList[]>(s, AUTH_ROUTES.masters) : Promise.resolve([]),

    get: (s: ApiSettings, id: string) => request<MasterList>(s, masterPath(id)),

    /** Editor or Approver. 409 `master_exists`; 400 `validation_failed` with `problems`. */
    create: (s: ApiSettings, body: MasterCreateRequest) =>
      request<MasterList>(s, AUTH_ROUTES.masters, { method: 'POST', body: JSON.stringify(body) }),

    update: (s: ApiSettings, id: string, patch: MasterUpdateRequest) =>
      request<MasterList>(s, masterPath(id), { method: 'PATCH', body: JSON.stringify(patch) }),

    /** 409 `master_in_use` while the active rule set references the list (`ApiError.code`). */
    remove: (s: ApiSettings, id: string) =>
      request<{ deleted: true; id: string }>(s, masterPath(id), { method: 'DELETE' }),

    items: (s: ApiSettings, id: string, q: { search?: string; limit?: number; cursor?: string } = {}) => {
      const qs = new URLSearchParams();
      if (q.search) qs.set('search', q.search);
      if (q.limit !== undefined) qs.set('limit', String(q.limit));
      if (q.cursor) qs.set('cursor', q.cursor);
      const tail = qs.toString();
      return request<MasterItemsPage>(s, masterPath(id, `/items${tail ? `?${tail}` : ''}`));
    },

    /** Idempotent: an existing value answers `added: false`. R11: `attrs` = the template's columns. */
    addItem: (s: ApiSettings, id: string, value: string, note?: string, attrs?: MasterAttrs) =>
      request<MasterAddItemResponse>(s, masterPath(id, '/items'), {
        method: 'POST',
        body: JSON.stringify({ value, ...(note ? { note } : {}), ...(attrs && Object.keys(attrs).length > 0 ? { attrs } : {}) }),
      }),

    removeItem: (s: ApiSettings, id: string, value: string) =>
      request<MasterRemoveItemResponse>(s, masterPath(id, `/items?value=${encodeURIComponent(value)}`), { method: 'DELETE' }),

    /**
     * `dryRun: true` previews and changes nothing. A real import with invalid rows is a 400
     * `invalid_rows` carrying the same result shape — the preview is shown first so that is rare.
     */
    importFile: (s: ApiSettings, id: string, file: File, mode: MasterImportMode, dryRun: boolean) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return request<MasterImportResult>(s, masterPath(id, `/import?mode=${mode}&dryRun=${dryRun ? 'true' : 'false'}`), {
        method: 'POST',
        body: form,
      });
    },

    /** `<id>.csv` exactly as the BFF wrote it (UTF-8 with BOM). */
    exportCsv: (s: ApiSettings, id: string) => request<Blob>(s, masterPath(id, '/export'), { responseType: 'blob' }),

    /** `<id>.xlsx`: the values in the template's sample shape (typed cells, a guide sheet). */
    exportXlsx: (s: ApiSettings, id: string) => request<Blob>(s, masterPath(id, '/export?format=xlsx'), { responseType: 'blob' }),

    // ---- R11 master templates ----
    /** Replaces one value's note and template columns. */
    updateItem: (s: ApiSettings, id: string, value: string, note: string | undefined, attrs: MasterAttrs) =>
      request<MasterItemUpdateResponse>(s, masterPath(id, `/items?value=${encodeURIComponent(value)}`), {
        method: 'PUT',
        body: JSON.stringify({ ...(note ? { note } : {}), attrs }),
      }),

    /** A list with no template takes one whose key type and field are its own — once. */
    assignTemplate: (s: ApiSettings, id: string, templateId: string) =>
      request<MasterList>(s, masterPath(id, '/template'), { method: 'POST', body: JSON.stringify({ templateId }) }),

    templates: {
      list: (s: ApiSettings): Promise<MasterTemplate[]> =>
        COOKIE_MODE ? request<MasterTemplate[]>(s, `${AUTH_ROUTES.masters}/templates`) : Promise.resolve([]),

      get: (s: ApiSettings, id: string) => request<MasterTemplate>(s, `${AUTH_ROUTES.masters}/templates/${encodeURIComponent(id)}`),

      create: (s: ApiSettings, body: MasterTemplateRequest) =>
        request<MasterTemplate>(s, `${AUTH_ROUTES.masters}/templates`, { method: 'POST', body: JSON.stringify(body) }),

      /** `etag` = the template's `_etag`; 409 `template_conflict` when it moved, `template_in_use` when lists depend on the change. */
      update: (s: ApiSettings, id: string, etag: string, body: MasterTemplateRequest) =>
        request<MasterTemplate>(s, `${AUTH_ROUTES.masters}/templates/${encodeURIComponent(id)}`, {
          method: 'PUT',
          headers: { 'If-Match': etag },
          body: JSON.stringify(body),
        }),

      remove: (s: ApiSettings, id: string, etag: string) =>
        request<{ deleted: true; id: string }>(s, `${AUTH_ROUTES.masters}/templates/${encodeURIComponent(id)}`, {
          method: 'DELETE',
          headers: { 'If-Match': etag },
        }),

      /** `<id>.sample.xlsx`: the header row the import reads and example rows valid for the template. */
      sampleXlsx: (s: ApiSettings, id: string) =>
        request<Blob>(s, `${AUTH_ROUTES.masters}/templates/${encodeURIComponent(id)}/sample.xlsx`, { responseType: 'blob' }),

      /** A template draft from an Excel file's header row and the rows under it. Nothing is saved. */
      infer: (s: ApiSettings, file: File) => {
        const form = new FormData();
        form.append('file', file, file.name);
        return request<MasterTemplateInferResult>(s, `${AUTH_ROUTES.masters}/templates/infer`, { method: 'POST', body: form });
      },
    },
  },

  // ---- maker-checker (cookie mode only) -----------------------------------
  // Unused while maker-checker is dropped (product-owner decision): the console publishes
  // directly through `putRules`. Kept so the flow can return without a rewrite.
  proposals: {
    list: (s: ApiSettings) => request<Proposal[]>(s, AUTH_ROUTES.proposals),

    get: (s: ApiSettings, id: string) => request<Proposal>(s, proposalPath(id)),

    /** Editor. The candidate is the whole document, exactly as a release would be. */
    create: (s: ApiSettings, ruleSet: RuleSet, comment?: string) =>
      request<Proposal>(s, AUTH_ROUTES.proposals, {
        method: 'POST',
        body: JSON.stringify({ ruleSet, ...(comment ? { comment } : {}) }),
      }),

    validate: (s: ApiSettings, id: string) =>
      request<ProposalValidateResponse>(s, proposalPath(id, '/validate'), { method: 'POST' }),

    replay: (s: ApiSettings, id: string) =>
      request<ProposalReplayResponse>(s, proposalPath(id, '/replay'), { method: 'POST' }),

    /** Approver, and never the author — the BFF answers 403 `maker_is_checker` if it is. */
    approve: (s: ApiSettings, id: string) => request<Proposal>(s, proposalPath(id, '/approve'), { method: 'POST' }),

    publish: (s: ApiSettings, id: string) =>
      request<ProposalPublishResponse>(s, proposalPath(id, '/publish'), { method: 'POST' }),
  },

  // R9 cases
  // ---- Investigate (cookie mode only) -------------------------------------
  // The BFF's `/api/cases` and `/api/investigate`. Password mode has no BFF: reads come back
  // empty and the screen offers no writes (it never calls them there). Writes that change what
  // an analyst decided on (a transition, assignee/priority) send the case's `_etag` as
  // If-Match, so a change made meanwhile is a 409, never silently overwritten.
  cases: {
    decisions: (s: ApiSettings, cardId: string, cursor?: string): Promise<InvestigatePage> =>
      COOKIE_MODE
        ? request<InvestigatePage>(s, `${CASE_ROUTES.decisions}${caseQuery({ cardId }, cursor)}`)
        : Promise.resolve({ cardId, source: 'none', items: [] }),

    list: (s: ApiSettings, filters: CaseFilters = {}, cursor?: string): Promise<CasePage> =>
      COOKIE_MODE ? request<CasePage>(s, `${CASE_ROUTES.cases}${caseQuery(filters, cursor)}`) : Promise.resolve({ items: [] }),

    get: (s: ApiSettings, cardId: string, id: string) => request<Case>(s, casePath(cardId, id)),

    /** 409 `case_exists` names the decision's case in its message; 404 `decision_not_found`. */
    open: (s: ApiSettings, cardId: string, decisionId: string, note?: string) =>
      request<Case>(s, CASE_ROUTES.cases, { method: 'POST', body: JSON.stringify({ cardId, decisionId, ...(note ? { note } : {}) }) }),

    update: (s: ApiSettings, c: Case, patch: { assignee?: string | null; priority?: CasePriority | null }) =>
      request<Case>(s, casePath(c.cardId, c.id), { method: 'PATCH', body: JSON.stringify(patch), headers: { 'If-Match': c._etag } }),

    /** 409 `invalid_transition` / `etag_mismatch`; 400 when a reopen has no note. */
    transition: (s: ApiSettings, c: Case, to: CaseStatus, note?: string) =>
      request<Case>(s, casePath(c.cardId, c.id, '/transitions'), {
        method: 'POST',
        body: JSON.stringify({ to, ...(note ? { note } : {}) }),
        headers: { 'If-Match': c._etag },
      }),

    addNote: (s: ApiSettings, c: Case, text: string) =>
      request<Case>(s, casePath(c.cardId, c.id, '/notes'), { method: 'POST', body: JSON.stringify({ text }) }),

    editNote: (s: ApiSettings, c: Case, noteId: string, text: string) =>
      request<Case>(s, casePath(c.cardId, c.id, `/notes/${encodeURIComponent(noteId)}`), { method: 'PATCH', body: JSON.stringify({ text }) }),

    deleteNote: (s: ApiSettings, c: Case, noteId: string) =>
      request<Case>(s, casePath(c.cardId, c.id, `/notes/${encodeURIComponent(noteId)}`), { method: 'DELETE' }),

    precision: (s: ApiSettings, range: { from?: string; to?: string } = {}): Promise<RulePrecisionReport> => {
      if (!COOKIE_MODE) return Promise.resolve({ cases: 0, rules: [] });
      const qs = new URLSearchParams();
      if (range.from) qs.set('from', range.from);
      if (range.to) qs.set('to', range.to);
      const tail = qs.toString();
      return request<RulePrecisionReport>(s, `${CASE_ROUTES.precision}${tail ? `?${tail}` : ''}`);
    },
  },
};

/**
 * The client every screen uses. With `VITE_DEMO=1` the read-only data endpoints — decisions,
 * stats, version history and the access trail — answer from `src/demo`
 * (merged in front of / behind the real data where there is some); every write, the rules
 * GET, validate, dry run and sign-in stay exactly as above. Off, this IS `realApi`.
 */
export const api: typeof realApi = DEMO ? withDemo<ApiSettings, typeof realApi>(realApi) : realApi;

/** The sample-scenario manifest written by scripts/copy-samples.mjs. */
export interface SampleScenario {
  file: string;
  name: string;
  requests: number;
  expected: string;
  transactionIds: string[];
}

/**
 * A sample's name in the console's language (UX review P2-9): the manifest's name is the
 * English file title; a sample the catalogue knows (`dryrun.sample.<file>.name`) reads in Thai
 * too, and one it does not know yet keeps the manifest's name.
 */
export function sampleName(s: Pick<SampleScenario, 'file' | 'name'>): string {
  const stem = s.file.replace(/\.json$/, '');
  return hasKey(`dryrun.sample.${stem}.name`) ? t(`dryrun.sample.${stem}.name`) : s.name;
}

/**
 * Static assets served next to the SPA, not API routes — so `useBaseUrl: false` (they must
 * not follow a relocated API) and `auth: false` (no credential belongs on them). They still
 * go through `request()` so the fetch inventory stays complete and has one error shape.
 *
 * The leading `/` is load-bearing. These paths used to be relative, which resolves against
 * the CURRENT route: from `/rules/transaction` the browser asked for
 * `/rules/samples/index.json`, the BFF's SPA fallback answered `200 text/html`, `request()`
 * fell back to returning the raw text, and `DryRunTab` called `.find` on a string — the
 * whole console unmounted to a white screen. It only bites where the SPA is served from a
 * deep route, which is exactly what `dotnet/admin-api/wwwroot` is.
 */
const NO_AUTH = { auth: false, useBaseUrl: false } as const;

export async function loadSampleIndex(): Promise<SampleScenario[]> {
  const body = await request<SampleScenario[]>(DEFAULT_SETTINGS, '/samples/index.json', NO_AUTH);
  // Belt and braces: anything that is not the manifest is "no scenarios", never a crash.
  return Array.isArray(body) ? body : [];
}

export async function loadSample(file: string): Promise<PreDetectRequest[]> {
  const body = await request<PreDetectRequest | PreDetectRequest[]>(DEFAULT_SETTINGS, `/samples/${file}`, NO_AUTH);
  return Array.isArray(body) ? body : [body];
}

// =============================================================================
// R8 users — console users and roles, and the signed-in user's own password
// =============================================================================
// Kept as one appended block (its imports included) so the other streams' edits to this file
// never touch it. Cookie mode only: password mode has no admin service and no users.

import { withDemoUsers } from './demo/users';
import type { AuthProviders, ConsoleUser, UserCreateRequest, UserUpdateRequest } from './users';

export const USER_ROUTES = {
  /** Admin only (`CanManageUsers`). */
  users: '/api/users',
  /** Any signed-in user; the one route besides sign-in a temporary password may call. */
  mePassword: '/api/me/password',
  /** Anonymous: which sign-in providers are configured (Entra is configuration only for now). */
  providers: '/api/auth/providers',
} as const;

const userPath = (id: string, suffix = ''): string => `${USER_ROUTES.users}/${encodeURIComponent(id)}${suffix}`;

const realUsersApi = {
  list: (s: ApiSettings): Promise<ConsoleUser[]> =>
    COOKIE_MODE ? request<ConsoleUser[]>(s, USER_ROUTES.users) : Promise.resolve([]),

  get: (s: ApiSettings, id: string) => request<ConsoleUser>(s, userPath(id)),

  /** 409 `user_exists`; 400 `validation_failed` with `problems`. The user must change the password at first sign-in. */
  create: (s: ApiSettings, body: UserCreateRequest) =>
    request<ConsoleUser>(s, USER_ROUTES.users, { method: 'POST', body: JSON.stringify(body) }),

  /** `etag` from the row: 409 `etag_mismatch` if somebody changed the user since; 409 guards (`last_admin`, …). */
  update: (s: ApiSettings, id: string, patch: UserUpdateRequest, etag: string) =>
    request<ConsoleUser>(s, userPath(id), { method: 'PATCH', body: JSON.stringify(patch), headers: { 'If-Match': etag } }),

  /** A new temporary password; also clears a lockout. */
  resetPassword: (s: ApiSettings, id: string, temporaryPassword: string) =>
    request<ConsoleUser>(s, userPath(id, '/reset-password'), { method: 'POST', body: JSON.stringify({ temporaryPassword }) }),

  /** 409 `cannot_delete_self` / `last_admin`. */
  remove: (s: ApiSettings, id: string, etag?: string) =>
    request<{ deleted: true; id: string }>(s, userPath(id), { method: 'DELETE', ...(etag ? { headers: { 'If-Match': etag } } : {}) }),

  /** 400 `current_password_wrong` / `password_unchanged` / `validation_failed`. */
  changeOwnPassword: (s: ApiSettings, currentPassword: string, newPassword: string) =>
    request<{ ok: boolean; mustChangePassword: boolean }>(s, USER_ROUTES.mePassword, {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  providers: (s: ApiSettings): Promise<AuthProviders | null> =>
    COOKIE_MODE ? request<AuthProviders>(s, USER_ROUTES.providers) : Promise.resolve(null),
};

export type UsersApi = typeof realUsersApi;

/** With `VITE_DEMO=1` a few read-only demo users stand in front of the real ones (`demo/users.ts`). */
export const usersApi: UsersApi = DEMO ? withDemoUsers<ApiSettings, UsersApi>(realUsersApi) : realUsersApi;

// =============================================================================
// R3 webhooks W1 — webhook connections ("call an API like Postman")
// =============================================================================
// One appended block (its imports included), like R8's. Cookie mode only: password mode has no
// admin service, so the list is empty there and the screen offers no writes.

import { withDemoWebhooks } from './demo/webhooks';
import {
  WEBHOOK_ROUTES,
  webhookPath,
  type DeliveryFilters,
  type WebhookDeliveryPage,
  type WebhookPreview,
  type WebhookTestResult,
  type WebhookView,
  type WebhookWrite,
} from './webhooks';

const realWebhooksApi = {
  list: (s: ApiSettings): Promise<WebhookView[]> =>
    COOKIE_MODE ? request<WebhookView[]>(s, WEBHOOK_ROUTES.webhooks) : Promise.resolve([]),

  get: (s: ApiSettings, id: string) => request<WebhookView>(s, webhookPath(id)),

  /** Editor or Approver. 409 `webhook_exists`; 400 `validation_failed` with `problems` (the SSRF check among them). */
  create: (s: ApiSettings, body: WebhookWrite) =>
    request<WebhookView>(s, WEBHOOK_ROUTES.webhooks, { method: 'POST', body: JSON.stringify(body) }),

  /** The whole connection; a secret left out keeps its stored value. 409 `etag_mismatch`. */
  update: (s: ApiSettings, id: string, body: WebhookWrite, etag: string) =>
    request<WebhookView>(s, webhookPath(id), { method: 'PUT', body: JSON.stringify(body), headers: { 'If-Match': etag } }),

  /** 409 `webhook_in_use` names the alert rules (`ApiError.message`). */
  remove: (s: ApiSettings, id: string, etag?: string) =>
    request<{ deleted: true; id: string }>(s, webhookPath(id), { method: 'DELETE', ...(etag ? { headers: { 'If-Match': etag } } : {}) }),

  /** Renders a template against the sample decision. Sends nothing. */
  preview: (s: ApiSettings, body: { bodyTemplate: string; contentType: string; method: string }, signal?: AbortSignal) =>
    request<WebhookPreview>(s, WEBHOOK_ROUTES.preview, { method: 'POST', body: JSON.stringify(body), ...(signal ? { signal } : {}) }),

  /** One real request through the dispatcher. 200 with `ok: false` = the target refused — not an error of this call. */
  test: (s: ApiSettings, id: string, body: { alertRuleId?: string } = {}) =>
    request<WebhookTestResult>(s, webhookPath(id, '/test'), { method: 'POST', body: JSON.stringify(body) }),

  deliveries: (s: ApiSettings, id: string, filters: DeliveryFilters = {}, cursor?: string): Promise<WebhookDeliveryPage> => {
    if (!COOKIE_MODE) return Promise.resolve({ webhookId: id, source: 'none', items: [] });
    const qs = new URLSearchParams();
    if (filters.status) qs.set('status', filters.status);
    if (filters.test !== undefined) qs.set('test', String(filters.test));
    if (cursor) qs.set('cursor', cursor);
    const tail = qs.toString();
    return request<WebhookDeliveryPage>(s, webhookPath(id, `/deliveries${tail ? `?${tail}` : ''}`));
  },
};

export type WebhooksApi = typeof realWebhooksApi;

/** With `VITE_DEMO=1` two read-only demo connections stand in front of the real ones (`demo/webhooks.ts`). */
export const webhooksApi: WebhooksApi = DEMO ? withDemoWebhooks<ApiSettings, WebhooksApi>(realWebhooksApi) : realWebhooksApi;
// end R3 webhooks W1

// =============================================================================
// 2c rule sets — the registry (`/api/rulesets`)
// =============================================================================
// One appended block (its imports included). The rule calls of ONE set are the existing
// `api.getRules/putRules/…` with `settings.ruleSetId` (`rulesRoutesFor`). Password mode has no
// admin service: the registry reads as the decision API's one set and offers no writes.

import type { RuleSetStatus } from '@kkp-fraud/shared';
import { demoRuleSetDocument, demoRuleSetVersions, isDemoRuleSetId, mergeRuleSets } from './demo/ruleSets';
import { RULE_SET_ROUTES, ruleSetPath, type RuleSetCreateRequest, type RuleSetList, type RuleSetUpdateRequest } from './ruleSets';
import type { RuleSetDuplicateRequest } from './ruleSets'; // Wave 5

const ifMatch = (etag: string) => ({ 'If-Match': etag });

const realRuleSetsApi = {
  /** Cookie mode: the registry. Password mode: the decision API's one set, read only. */
  list: async (s: ApiSettings): Promise<RuleSetList> => {
    if (COOKIE_MODE) return request<RuleSetList>(s, RULE_SET_ROUTES.ruleSets);
    const { ruleSet } = await realApi.getRules({ baseUrl: s.baseUrl });
    const n = (ruleSet.ruleBased ?? []).length + (ruleSet.patternBased ?? []).length;
    return {
      source: 'proxy',
      canManage: false,
      etag: '',
      sets: [
        {
          id: ruleSet.ruleSetId,
          name: ruleSet.ruleSetId,
          enabled: true,
          primary: true,
          archived: false,
          version: ruleSet.version,
          ruleCount: n,
          enabledRuleCount: [...(ruleSet.ruleBased ?? []), ...(ruleSet.patternBased ?? [])].filter((r) => r.enabled).length,
          setRuleCount: (ruleSet.setRules ?? []).length,
        },
      ],
    };
  },

  /** Editor or Approver. Created switched off unless `enabled`; released at once as 1.0.0. 409 `rule_set_exists`. */
  create: (s: ApiSettings, body: RuleSetCreateRequest) =>
    request<RuleSetList>(s, RULE_SET_ROUTES.ruleSets, { method: 'POST', body: JSON.stringify(body) }),

  /**
   * Wave 5, Admin only: a new set, switched off, released empty as 1.0.0 like a create; its entry
   * records `copiedFrom: {setId, version}` (the source's live release), which its page publishes
   * as the first real release. 404 `unknown_rule_set` (source), 409 `rule_set_exists`.
   */
  duplicate: (s: ApiSettings, sourceId: string, body: RuleSetDuplicateRequest) =>
    request<RuleSetList>(s, ruleSetPath(sourceId, '/duplicate'), { method: 'POST', body: JSON.stringify(body) }),

  /** `etag` = the list's. 409 `registry_conflict` (somebody changed the sets), `rule_set_primary`, `rule_set_archived`. */
  update: (s: ApiSettings, id: string, patch: RuleSetUpdateRequest, etag: string) =>
    request<RuleSetList>(s, ruleSetPath(id), { method: 'PATCH', body: JSON.stringify(patch), headers: ifMatch(etag) }),

  /** Only a switched-off set that is not the primary (409 `rule_set_enabled` / `rule_set_primary`). */
  archive: (s: ApiSettings, id: string, etag: string) =>
    request<RuleSetList>(s, ruleSetPath(id, '/archive'), { method: 'POST', headers: ifMatch(etag) }),

  /** Back from the archive, switched off. */
  restore: (s: ApiSettings, id: string, etag: string) =>
    request<RuleSetList>(s, ruleSetPath(id, '/restore'), { method: 'POST', headers: ifMatch(etag) }),

  /** Moves the primary flag (it supplies the fail-safe policy). 409 `rule_set_disabled` / `rule_set_not_released`. */
  makePrimary: (s: ApiSettings, id: string, etag: string) =>
    request<RuleSetList>(s, ruleSetPath(id, '/primary'), { method: 'POST', headers: ifMatch(etag) }),

  /** The engine's own view: which sets it loaded (`/readyz` `ruleSets`); `[]` from an engine before 2c. */
  engineStatus: async (s: ApiSettings): Promise<RuleSetStatus[]> => {
    const r = await realApi.ready({ baseUrl: s.baseUrl });
    return Array.isArray(r.ruleSets) ? r.ruleSets : [];
  },
};

export type RuleSetsApi = typeof realRuleSetsApi;

/** With `VITE_DEMO=1` two read-only demo sets follow the real ones (`demo/ruleSets.ts`). */
export const ruleSetsApi: RuleSetsApi = DEMO
  ? {
      ...realRuleSetsApi,
      list: async (s: ApiSettings) => {
        let real: RuleSetList | null = null;
        try {
          real = await realRuleSetsApi.list(s);
        } catch (e) {
          console.warn('[demo] GET rule sets failed upstream; showing demo data only.', e);
        }
        return mergeRuleSets(real, Date.now());
      },
    }
  : realRuleSetsApi;
// end 2c rule sets

// =============================================================================
// Wave 4 API clients — one key per caller of the decision API (`/api/api-clients`)
// =============================================================================
// One appended block (its imports included). Admin only, cookie mode only: password mode has no
// admin service, so the list is empty there and the screen offers no writes.

import type { ApiClientsStatus } from '@kkp-fraud/shared';
import { withDemoApiClients } from './demo/apiClients';
import {
  API_CLIENT_ROUTES,
  apiClientPath,
  type ApiClientCreateRequest,
  type ApiClientUpdateRequest,
  type ApiClientView,
  type ApiClientWithKey,
  type ApiReferenceInfo,
  type ApiTryRequest,
  type ApiTryResult,
} from './apiClients';

const realApiClientsApi = {
  list: (s: ApiSettings): Promise<ApiClientView[]> =>
    COOKIE_MODE ? request<ApiClientView[]>(s, API_CLIENT_ROUTES.clients) : Promise.resolve([]),

  /** 201 with the key — shown once. 409 `api_client_exists`; 400 `validation_failed` with coded `problems`. */
  create: (s: ApiSettings, body: ApiClientCreateRequest) =>
    request<ApiClientWithKey>(s, API_CLIENT_ROUTES.clients, { method: 'POST', body: JSON.stringify(body) }),

  /** 409 `etag_mismatch` / `api_client_revoked`. */
  update: (s: ApiSettings, id: string, patch: ApiClientUpdateRequest, etag: string) =>
    request<ApiClientView>(s, apiClientPath(id), { method: 'PATCH', body: JSON.stringify(patch), headers: { 'If-Match': etag } }),

  /** A new key, shown once; the old one stops working at the engine's next poll. */
  rotate: (s: ApiSettings, id: string, etag: string) =>
    request<ApiClientWithKey>(s, apiClientPath(id, '/rotate'), { method: 'POST', headers: { 'If-Match': etag } }),

  /** Final: the client stays listed (its id is in the audit trail) but never authenticates again. */
  revoke: (s: ApiSettings, id: string, etag: string) =>
    request<ApiClientView>(s, apiClientPath(id, '/revoke'), { method: 'POST', headers: { 'If-Match': etag } }),

  /** What the decision API has loaded (`/readyz` `apiClients`), or null from an engine before Wave 4. */
  engineStatus: async (s: ApiSettings): Promise<ApiClientsStatus | null> => {
    const r = await realApi.ready({ baseUrl: s.baseUrl });
    return r.apiClients ?? null;
  },

  /** API reference: the public decision API a caller uses, and the Try-it cap. */
  reference: (s: ApiSettings) => request<ApiReferenceInfo>(s, API_CLIENT_ROUTES.reference),

  /**
   * Try it: the admin service sends `body` to `/v1/predetect` with `key` and answers the decision
   * API's own status inside a 200 (an upstream 401 is `status: 401`, never a session-ending 401).
   * 429 `try_rate_limited`; 502 `upstream_unreachable`.
   */
  tryPredetect: (s: ApiSettings, req: ApiTryRequest) =>
    request<ApiTryResult>(s, API_CLIENT_ROUTES.try, { method: 'POST', body: JSON.stringify(req) }),
};

export type ApiClientsApi = typeof realApiClientsApi;

/** With `VITE_DEMO=1` three read-only demo clients stand in front of the real ones (`demo/apiClients.ts`). */
export const apiClientsApi: ApiClientsApi = DEMO ? withDemoApiClients<ApiSettings, ApiClientsApi>(realApiClientsApi) : realApiClientsApi;
// end Wave 4 API clients
